/**
 * Pure helpers for the library settings screens (คำตอบสำเร็จรูป · คลังรูปและวิดีโอ) — no DOM, unit-tested in
 * test/library-config.spec.ts. Rules mirror the server (api/app/library.py) so the forms can warn early.
 */
import type { AiIntent, AnswerTypeInfo, CannedItem, MediaAsset, Placeholder } from '../api/types'
import { durationLabel, sizeLabel } from './media'

export const MAX_ANSWER_MEDIA = 4
/** Instagram takes ≤ 1,000 UTF-8 bytes per message (Thai ≈ 330 characters). */
export const IG_MAX_BYTES = 1000

export const CHANNEL_LABEL: Record<string, string> = { line: 'LINE', fb: 'Messenger', ig: 'Instagram', web: 'แชทบนเว็บ' }

/** Order the owner sees: active answers first, then by the saved order, then the most used. */
export function sortCanned(items: CannedItem[]): CannedItem[] {
  return [...items].sort(
    (a, b) => Number(b.is_active) - Number(a.is_active) || a.sort - b.sort || b.use_count - a.use_count || a.id.localeCompare(b.id),
  )
}

export function firstLine(text: string): string {
  return (text.split('\n').find((l) => l.trim()) ?? '').trim()
}

/** First line, shortened as text (never by CSS clipping): ≤ `max` characters, cut after a space when one is near. */
export function excerpt(text: string, max = 110): string {
  const line = firstLine(text)
  const chars = [...line]
  if (chars.length <= max) return line
  const head = chars.slice(0, max).join('')
  const space = head.lastIndexOf(' ')
  return `${(space > max * 0.7 ? head.slice(0, space) : head).trimEnd()}…`
}

/** "เมนู, ราคา" from intent keys (unknown keys are skipped). */
export function intentLabels(keys: string[], all: AiIntent[]): string {
  return keys
    .map((k) => all.find((i) => i.key === k)?.label)
    .filter((x): x is string => !!x)
    .join(', ')
}

export interface IntentGroup {
  group: string
  label: string
  blocked: boolean
  items: AiIntent[]
}

const GROUP_ORDER = ['menu', 'shipping', 'care_status', 'other', 'blocked', 'script', 'playbook']

/** Intents grouped like the taxonomy (menu · shipping · care · other), blocked topics, then the script's own types. */
export function groupIntents(all: AiIntent[]): IntentGroup[] {
  const out: IntentGroup[] = []
  for (const i of all) {
    let g = out.find((x) => x.group === i.group)
    if (!g) {
      g = { group: i.group, label: i.group_label, blocked: i.blocked, items: [] }
      out.push(g)
    }
    g.items.push(i)
  }
  const rank = (g: string) => (GROUP_ORDER.includes(g) ? GROUP_ORDER.indexOf(g) : GROUP_ORDER.length - 1)
  return out.sort((a, b) => rank(a.group) - rank(b.group))
}

/** "/Menu " → "menu" (what the server stores). */
export function normalizeShortcut(raw: string): string {
  return raw.trim().replace(/^\/+/, '').trim().toLowerCase()
}

/** Thai reason the shortcut is not allowed, or null. */
export function shortcutProblem(raw: string, taken: string[] = []): string | null {
  const s = normalizeShortcut(raw)
  if (!s) return 'ใส่คำสั่งลัด เช่น menu'
  if (/[\s/]/.test(s)) return 'คำสั่งลัดต้องเป็นคำเดียว ไม่มีช่องว่างหรือเครื่องหมาย /'
  if ([...s].length > 32) return 'คำสั่งลัดยาวได้ไม่เกิน 32 ตัวอักษร'
  if (taken.includes(s)) return `มีคำสั่ง /${s} อยู่แล้ว ใช้คำอื่น`
  return null
}

/** The text with every placeholder replaced by its sample (preview in the editor). */
export function previewText(body: string, placeholders: Placeholder[]): string {
  let out = body
  for (const p of placeholders) out = out.split(p.key).join(p.sample)
  return out
}

export function utf8Bytes(text: string): number {
  return new TextEncoder().encode(text).length
}

export function tooLongForInstagram(text: string): boolean {
  return utf8Bytes(text) > IG_MAX_BYTES
}

/** Insert `piece` at the caret (replacing a selection), with a space when it would glue to a word. */
export function insertAt(text: string, piece: string, start: number, end = start): { text: string; caret: number } {
  const before = text.slice(0, start)
  const after = text.slice(end)
  const pad = before && !/\s$/.test(before) ? ' ' : ''
  const next = `${before}${pad}${piece}${after}`
  return { text: next, caret: before.length + pad.length + piece.length }
}

export function moveItem<T>(list: T[], from: number, to: number): T[] {
  const out = [...list]
  if (from < 0 || from >= out.length || to < 0 || to >= out.length || from === to) return out
  const [x] = out.splice(from, 1)
  out.splice(to, 0, x as T)
  return out
}

/** Search by title + folder chip (null = every folder). */
export function filterMedia(items: MediaAsset[], opts: { q?: string; folder?: string | null }): MediaAsset[] {
  const needle = (opts.q ?? '').trim().toLowerCase()
  return items.filter(
    (m) =>
      !m.deleted &&
      (opts.folder === null || opts.folder === undefined || m.folder === opts.folder) &&
      (!needle || m.title.toLowerCase().includes(needle) || m.folder.toLowerCase().includes(needle)),
  )
}

/** "รูป · 480 KB" / "วิดีโอ · 0:42 · 18 MB" */
export function mediaMeta(m: Pick<MediaAsset, 'kind' | 'bytes' | 'duration_ms'>): string {
  const parts = [m.kind === 'video' ? 'วิดีโอ' : 'รูป']
  const len = m.kind === 'video' ? durationLabel(m.duration_ms) : null
  if (len) parts.push(len)
  parts.push(sizeLabel(m.bytes))
  return parts.join(' · ')
}

/** Tile line: "รูป · 480 KB" / "วิดีโอ · 18 MB" (the thumbnail already shows a video's length). */
export function tileMeta(m: Pick<MediaAsset, 'kind' | 'bytes'>): string {
  return `${m.kind === 'video' ? 'วิดีโอ' : 'รูป'} · ${sizeLabel(m.bytes)}`
}

/** "1080 × 1350" or null. */
export function dimensions(m: Pick<MediaAsset, 'width' | 'height'>): string | null {
  return m.width && m.height ? `${m.width} × ${m.height}` : null
}

/** "LINE: LINE ไม่แสดงรูป webp" lines for the limits of a file. */
export function limitLines(m: Pick<MediaAsset, 'limits'>): { channel: string; label: string; reason: string }[] {
  return m.limits.map((l) => ({ channel: l.channel, label: CHANNEL_LABEL[l.channel] ?? l.channel, reason: l.reason }))
}

// ------------------------------------------------ the owner's answers for the suggested script (owner 17 ต.ค. 69)

/** The "/" picker of a chat: active answers it can fill — not one holding a chat's data only the script fills. */
export function pickerList(items: CannedItem[]): CannedItem[] {
  return items.filter((r) => r.is_active && !r.chat_only)
}

export interface TypeCoverage {
  key: string
  label: string
  script: boolean
  active: number
  /** Unreviewed drafts of this type that are switched off (the editor opens the first). */
  drafts: CannedItem[]
}

/** Every answer type of the suggested script with its active answers and drafts, from the list on screen (so a switch
 *  flipped on this page counts at once). */
export function typeCoverage(types: AnswerTypeInfo[], items: CannedItem[]): TypeCoverage[] {
  return types.map((t) => {
    const mine = items.filter((r) => r.intents.includes(t.key))
    return {
      key: t.key,
      label: t.label,
      script: t.script,
      active: mine.filter((r) => r.is_active).length,
      drafts: mine.filter((r) => r.is_draft && !r.is_active),
    }
  })
}

/** The placeholders of the editor: the ones filled everywhere, then the chat's data only the suggested script fills. */
export function groupPlaceholders(all: Placeholder[]): { everywhere: Placeholder[]; chat: Placeholder[] } {
  return { everywhere: all.filter((p) => (p.scope ?? 'all') === 'all'), chat: all.filter((p) => p.scope === 'chat') }
}

/** "{ค่าส่งจ้า}" — "{…}" in a text that is not one of the placeholders (the server refuses to save it). */
export function unknownPlaceholders(body: string, all: Placeholder[]): string[] {
  const known = new Set(all.map((p) => p.key))
  return [...new Set(body.match(/\{[^{}\s]{1,30}\}/g) ?? [])].filter((t) => !known.has(t))
}
